/**
 * rosa.js — Navigazione, conteggio Rosa, CRUD Preso
 */

import { upsertRosterPlayer, deleteRosterPlayer } from '../services/db/roster.pocketbase.js';
import { deleteAuctionExit } from '../services/db/auctionExits.pocketbase.js';
import { EXIT_MODE_ASSIGNED } from '../services/db/auctions.pocketbase.js';
import { opponentPlaceholder } from './auctionOpponents.js';
import { escapeHtml } from '../utils/escapeHtml.js';

/** Chip «Io» nel form assegnazione (modalità assegnate). */
export const PRESO_SIDE_ME = '__me__';
/** Chip «Altri» nel form assegnazione (modalità semplice). */
export const PRESO_SIDE_OTHERS = '__others__';

export const rosaMethods = {
  /**
   * Naviga tra le schermate dell'asta attiva.
   * @param {'lista'|'studio'|'rosa'|'rose-altri'|'analytics'} screen
   */
  switchAuctionScreen(screen) {
    if (!this.state.activeAuctionId && screen !== 'lista') {
      this.showUnifiedSetupScreen?.();
      return;
    }

    const screenLista = document.getElementById('screen-lista');
    const screenStudio = document.getElementById('screen-studio');
    const screenRosa = document.getElementById('screen-rosa');
    const screenRoseAltri = document.getElementById('screen-rose-altri');
    const screenAnalytics = document.getElementById('screen-analytics');

    if (screenLista) screenLista.hidden = true;
    if (screenStudio) screenStudio.hidden = true;
    if (screenRosa) screenRosa.hidden = true;
    if (screenRoseAltri) screenRoseAltri.hidden = true;
    if (screenAnalytics) screenAnalytics.hidden = true;

    const scrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth';

    switch (screen) {
      case 'studio': {
        if (!screenStudio) return;
        this.hideStudioInlineForm?.();
        screenStudio.hidden = false;
        this.renderStudioView?.();
        window.scrollTo({ top: 0, behavior: scrollBehavior });
        break;
      }
      case 'rosa': {
        if (!screenRosa) return;
        screenRosa.hidden = false;
        this.renderRosaView?.();
        window.scrollTo({ top: 0, behavior: scrollBehavior });
        break;
      }
      case 'rose-altri': {
        if (!screenRoseAltri) return;
        screenRoseAltri.hidden = false;
        this.renderRoseAltriView?.();
        window.scrollTo({ top: 0, behavior: scrollBehavior });
        break;
      }
      case 'analytics': {
        if (!screenAnalytics) return;
        screenAnalytics.hidden = false;
        this.renderAnalyticsView?.();
        window.scrollTo({ top: 0, behavior: scrollBehavior });
        break;
      }
      case 'lista':
      default: {
        if (screenLista) screenLista.hidden = false;
        this.renderListaKpiStrip?.();
        break;
      }
    }

    this.syncAppShellNav?.();
  },

  updateRosaTabCount() {
    const el = document.getElementById('rosa-count');
    if (el) el.textContent = this.state.roster ? this.state.roster.size : 0;
    this.syncAppShellCounts?.();
  },

  /** @deprecated Usa loadAuctionData via auctionSession */
  async loadRoster() {
    if (typeof this.loadAuctionData === 'function') {
      await this.loadAuctionData();
      this.updateRosaTabCount();
    }
  },

  /** @deprecated Settings su asta attiva */
  async loadRosaSettings() {
    // no-op: settings applicati in activateAuction / ensureActiveAuction
  },

  /**
   * Acquisisce un giocatore nella rosa dell'asta attiva.
   * @param {string} playerKey
   * @param {number} price
   */
  async acquirePlayer(playerKey, price) {
    if (!this.state.userSession || !this.state.activeAuctionId) return;
    const accessKey = this.state.userSession.getAccessCode();
    const auctionId = this.state.activeAuctionId;
    try {
      await upsertRosterPlayer({ accessKey, auctionId, playerKey, price });
      const prevAt = Number(this.state.roster.get(playerKey)?.at) || 0;
      this.state.roster.set(playerKey, { price, at: prevAt || Date.now() });
      if (this.state.auctionExits instanceof Map && this.state.auctionExits.has(playerKey)) {
        await deleteAuctionExit(accessKey, auctionId, playerKey);
        this.state.auctionExits.delete(playerKey);
      }
      this.updateRosaTabCount();
      if (typeof this.syncAuctionModeListFilters === 'function') {
        this.syncAuctionModeListFilters();
      }
      if (typeof this.syncStudioButtonsInTable === 'function') {
        this.syncStudioButtonsInTable();
      }
      this.renderRecentTakesStrip?.();
      this.renderListaKpiStrip?.();
      const rosaScreen = document.getElementById('screen-rosa');
      if (rosaScreen && !rosaScreen.hidden) {
        this.renderRosaView?.();
      }
      const studioScreen = document.getElementById('screen-studio');
      if (studioScreen && !studioScreen.hidden) {
        this.renderStudioView?.();
      }
      const analyticsScreen = document.getElementById('screen-analytics');
      if (analyticsScreen && !analyticsScreen.hidden) {
        this.renderAnalyticsView?.();
      }
    } catch (err) {
      console.error('[Rosa] Errore acquisto giocatore:', err);
      throw err;
    }
  },

  /**
   * Rimuove un giocatore dalla Rosa (non annulla exit asta).
   * @param {string} playerKey
   */
  async removeFromRoster(playerKey) {
    if (!this.state.userSession || !this.state.activeAuctionId) return;
    const accessKey = this.state.userSession.getAccessCode();
    const auctionId = this.state.activeAuctionId;
    try {
      await deleteRosterPlayer(accessKey, auctionId, playerKey);
      this.state.roster.delete(playerKey);
      this.updateRosaTabCount();
      if (typeof this.syncAuctionModeListFilters === 'function') {
        this.syncAuctionModeListFilters();
      }
      if (typeof this.syncStudioButtonsInTable === 'function') {
        this.syncStudioButtonsInTable();
      }
      this.renderRecentTakesStrip?.();
      this.renderListaKpiStrip?.();
      const rosaScreen = document.getElementById('screen-rosa');
      if (rosaScreen && !rosaScreen.hidden) {
        this.renderRosaView?.();
      }
      const studioScreen = document.getElementById('screen-studio');
      if (studioScreen && !studioScreen.hidden) {
        this.renderStudioView?.();
      }
      const analyticsScreen = document.getElementById('screen-analytics');
      if (analyticsScreen && !analyticsScreen.hidden) {
        this.renderAnalyticsView?.();
      }
    } catch (err) {
      console.error('[Rosa] Errore rimozione dalla Rosa:', err);
      throw err;
    }
  },

  /**
   * Form prezzo / assegnazione (me o altri).
   * In modalità assegnate: stesso modal con chip «Io» + avversari.
   * @param {object} [opts]
   * @param {'me'|'others'} [opts.mode] preselezione iniziale
   * @param {string|null} [opts.opponentId] preselezione avversario
   * @param {number|null} [opts.initialPrice] prezzo iniziale (modifica)
   * @param {boolean} [opts.allowUndo] mostra «Annulla assegnazione»
   */
  showPresoForm(playerKey, playerInfo, anchorEl, {
    mode = 'me',
    opponentId,
    initialPrice = null,
    allowUndo = false,
  } = {}) {
    if (!this.state.activeAuctionId) {
      this.showUnifiedSetupScreen?.();
      return;
    }
    document.querySelector('.preso-form-overlay')?.remove();

    const overlay = document.createElement('div');
    overlay.className = 'preso-form-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', allowUndo ? 'Modifica assegnazione' : 'Assegna giocatore');

    const name = playerInfo?.name || playerKey;
    const team = playerInfo?.team || '';
    const nameHtml = escapeHtml(name);
    const teamHtml = team ? escapeHtml(team) : '';
    const assignedMode = this.state.rosaExitMode === EXIT_MODE_ASSIGNED;
    const opponents = Array.isArray(this.state.auctionOpponents)
      ? this.state.auctionOpponents
      : [];
    const inRoster = this.state.roster instanceof Map && this.state.roster.has(playerKey);
    const exit = this.state.auctionExits?.get?.(playerKey);

    /** @type {string|null} PRESO_SIDE_ME | opponent id | null */
    let selectedSide = (() => {
      if (assignedMode) {
        if (mode === 'me' || inRoster) return PRESO_SIDE_ME;
        if (opponentId !== undefined) {
          return opponentId == null || opponentId === '' ? null : String(opponentId);
        }
        if (exit?.opponentId != null && String(exit.opponentId).trim()) {
          return String(exit.opponentId);
        }
        return null;
      }
      return mode === 'others' ? PRESO_SIDE_OTHERS : PRESO_SIDE_ME;
    })();

    const rosterPrice = Number(this.state.roster?.get?.(playerKey)?.price);
    const exitPrice = Number(exit?.price);
    const fallbackPrice = inRoster || mode === 'me' ? rosterPrice : exitPrice;
    const existingPrice = Number(
      initialPrice != null ? initialPrice : fallbackPrice,
    );
    const priceValue = Number.isFinite(existingPrice) && existingPrice >= 1
      ? String(existingPrice)
      : '';

    const title = allowUndo ? 'Modifica assegnazione' : 'Assegna giocatore';

    const chipsHtml = assignedMode
      ? `<div class="preso-form-field">
          <span class="preso-form-label">Chi l’ha preso</span>
          <div class="preso-opp-chips" role="group" aria-label="Assegna a">
            <button type="button" class="preso-opp-chip preso-opp-chip--me${selectedSide === PRESO_SIDE_ME ? ' is-on' : ''}"
              data-opp-id="${PRESO_SIDE_ME}" data-testid="preso-chip-me">Io</button>
            ${opponents.map((o, i) => {
              const id = String(o.id || '');
              const label = String(o.name || '').trim() || opponentPlaceholder(i);
              const on = selectedSide != null && String(selectedSide) === id;
              return `<button type="button" class="preso-opp-chip${on ? ' is-on' : ''}"
                data-opp-id="${escapeHtml(id)}">${escapeHtml(label)}</button>`;
            }).join('') || '<p class="preso-form-hint">Aggiungi avversari in Impostazioni asta.</p>'}
          </div>
        </div>`
      : `<div class="preso-form-field">
          <span class="preso-form-label">Chi l’ha preso</span>
          <div class="preso-opp-chips" role="group" aria-label="Assegna a">
            <button type="button" class="preso-opp-chip preso-opp-chip--me${selectedSide === PRESO_SIDE_ME ? ' is-on' : ''}"
              data-opp-id="${PRESO_SIDE_ME}" data-testid="preso-chip-me">Io</button>
            <button type="button" class="preso-opp-chip preso-opp-chip--others${selectedSide === PRESO_SIDE_OTHERS ? ' is-on' : ''}"
              data-opp-id="${PRESO_SIDE_OTHERS}" data-testid="preso-chip-others">Altri</button>
          </div>
        </div>`;

    const undoHtml = allowUndo
      ? `<button type="button" class="preso-form-btn preso-form-btn--undo" data-action="preso-undo-assignment"
           data-testid="preso-undo-assignment">Annulla assegnazione</button>`
      : '';

    overlay.dataset.playerKey = playerKey;
    if (name) overlay.dataset.playerName = name;
    if (team) overlay.dataset.playerTeam = team;

    overlay.innerHTML = `
      <div class="preso-form">
        <p class="preso-form-title">${title}: <strong>${nameHtml}</strong>${teamHtml ? ` (${teamHtml})` : ''}</p>
        <label class="preso-form-label" for="preso-price-input">Prezzo pagato (≥ 1)</label>
        <input id="preso-price-input" type="number" min="1" step="1" class="preso-form-input"
          placeholder="es. 25" autocomplete="off" value="${escapeHtml(priceValue)}" />
        ${chipsHtml}
        <div class="preso-form-actions">
          <button type="button" class="preso-form-btn preso-form-btn--confirm" data-action="preso-confirm">Conferma</button>
          <button type="button" class="preso-form-btn preso-form-btn--cancel" data-action="preso-cancel">${allowUndo ? 'Chiudi' : 'Annulla'}</button>
        </div>
        ${undoHtml}
        <p class="preso-form-error" hidden></p>
      </div>
    `;

    document.body.appendChild(overlay);

    const input = overlay.querySelector('#preso-price-input');
    const confirmBtn = overlay.querySelector('[data-action="preso-confirm"]');
    const cancelBtn = overlay.querySelector('[data-action="preso-cancel"]');
    const undoBtn = overlay.querySelector('[data-action="preso-undo-assignment"]');
    const errorEl = overlay.querySelector('.preso-form-error');

    const syncChipUi = () => {
      overlay.querySelectorAll('.preso-opp-chip').forEach((btn) => {
        const on = selectedSide != null
          && String(selectedSide) === btn.getAttribute('data-opp-id');
        btn.classList.toggle('is-on', on);
      });
    };

    overlay.querySelectorAll('.preso-opp-chip').forEach((btn) => {
      btn.addEventListener('click', () => {
        selectedSide = btn.getAttribute('data-opp-id');
        errorEl.hidden = true;
        syncChipUi();
      });
    });

    const close = () => overlay.remove();

    const confirm = async () => {
      const val = parseInt(input.value, 10);
      if (!val || val < 1) {
        errorEl.textContent = 'Inserisci un prezzo valido (≥ 1).';
        errorEl.hidden = false;
        input.focus();
        return;
      }
      if (assignedMode) {
        if (!opponents.length) {
          errorEl.textContent = 'Aggiungi almeno un avversario in Impostazioni asta.';
          errorEl.hidden = false;
          return;
        }
        if (!selectedSide) {
          errorEl.textContent = 'Scegli chi ha preso il giocatore (Io o un avversario).';
          errorEl.hidden = false;
          return;
        }
      }
      confirmBtn.disabled = true;
      confirmBtn.textContent = '...';
      errorEl.hidden = true;
      try {
        if (assignedMode) {
          if (selectedSide === PRESO_SIDE_ME) {
            await this.acquirePlayer(playerKey, val);
          } else {
            await this.markAuctionExited(playerKey, val, selectedSide);
          }
        } else if (selectedSide === PRESO_SIDE_OTHERS) {
          await this.markAuctionExited(playerKey, val);
        } else {
          await this.acquirePlayer(playerKey, val);
        }
        close();
      } catch (err) {
        errorEl.textContent = err?.message || 'Salvataggio non riuscito. Riprova.';
        errorEl.hidden = false;
        confirmBtn.disabled = false;
        confirmBtn.textContent = 'Conferma';
        input.focus();
      }
    };

    const undoAssignment = async () => {
      if (!undoBtn) return;
      undoBtn.disabled = true;
      undoBtn.textContent = '...';
      errorEl.hidden = true;
      try {
        if (this.state.roster?.has?.(playerKey)) {
          await this.removeFromRoster?.(playerKey);
        } else {
          await this.unmarkAuctionExited?.(playerKey);
        }
        close();
      } catch (err) {
        errorEl.textContent = err?.message || 'Annullamento non riuscito. Riprova.';
        errorEl.hidden = false;
        undoBtn.disabled = false;
        undoBtn.textContent = 'Annulla assegnazione';
      }
    };

    confirmBtn.addEventListener('click', confirm);
    cancelBtn.addEventListener('click', close);
    undoBtn?.addEventListener('click', undoAssignment);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') confirm();
      if (e.key === 'Escape') close();
    });

    requestAnimationFrame(() => {
      input.focus();
      if (priceValue) input.select();
    });
  },
};
